import { logout } from '..';
import { apiSlice } from './apiSlice';

export interface AuthRole {
  id: number;
  name: 'USER' | 'ADMIN' | 'SUPER_ADMIN' | 'MASTER' | string;
}

export interface LoginResponse {
  success: boolean;
  accessToken: string | null;
  type: string;
  role: AuthRole;
  passwordChanged: boolean;
  // mfa
  userId?: number;
  mfaRequired?: boolean;
  isTelegramEnabled?: boolean;
}
interface LoginCredentials {
  username: string;
  password: string;
  device: string;
}

interface ResetPasswordCredentials {
  mobile: string;
  newPassword: string;
  code: string;
}

interface DemoLoginResponse {
  accessToken: string;
  type: string;
}

interface SendOTPResponse {
  mobile: {
    sentAt: string;
    timeout: number;
    attempt: number;
    maxAttempt: number;
  };
}

interface LogoutResponse {
  message: string;
  error: string;
  statusCode: number;
}

interface RegisterCredentials {
  username: string;
  password: string;
  mobile?: string;
  dialCode?: string;
  country?: string;
  code?: string;
  mobileVerificationCode?: string;
  referralCode?: string;
}

interface RegisterResponse {
  accessToken: string;
  type: string;
}

export const authApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginCredentials>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth', 'Support', 'User'],
    }),
    loginDemoUser: builder.mutation<DemoLoginResponse, void>({
      query: () => ({
        url: '/auth/demo/login',
        method: 'POST',
      }),
      invalidatesTags: ['Auth', 'Support', 'User'],
    }),
    register: builder.mutation<RegisterResponse, RegisterCredentials>({
      query: (credentials) => ({
        url: '/auth/register',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth', 'User'],
    }),
    sendOtp: builder.mutation<SendOTPResponse, { mobile: string; country: string; type: string }>({
      query: (credentials) => ({
        url: '/auth/send-code',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),
    forgotPassword: builder.mutation<SendOTPResponse, { mobile: string }>({
      query: (credentials) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),
    resetPassword: builder.mutation<{ status: string }, ResetPasswordCredentials>({
      query: (credentials) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),
    logout: builder.mutation<LogoutResponse, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(apiSlice.util.resetApiState());
          dispatch(logout());
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginDemoUserMutation,
  useRegisterMutation,
  useSendOtpMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
} = authApi;
